import { useLocation } from 'react-router-dom';

interface BreadcrumbItem {
  name: string;
  item: string;
}

interface SchemaOrgProps {
  type: 'Organization' | 'WebSite' | 'WebPage' | 'BreadcrumbList' | 'Article' | 'FAQPage';
  data?: Record<string, unknown>;
}

function getBreadcrumbList(): Record<string, unknown> {
  const pathname = window.location.pathname;
  const items: BreadcrumbItem[] = [{ name: '首页', item: `${window.location.origin}/` }];

  if (pathname.startsWith('/category/')) {
    const slug = pathname.split('/category/')[1];
    const categoryMap: Record<string, string> = {
      all: '全部影视',
      movie: '电影',
      tv: '电视剧',
      anime: '动漫',
      variety: '综艺',
      documentary: '纪录片',
    };
    items.push({ name: categoryMap[slug] || slug, item: window.location.href });
  } else if (pathname.startsWith('/article/')) {
    items.push({ name: '影视详情', item: window.location.href });
  } else if (pathname === '/about') {
    items.push({ name: '关于我们', item: window.location.href });
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.item,
    })),
  };
}

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '88影视网',
  url: 'https://www.88yingshi.com',
  description: '88影视网提供高清影视在线观看资源，涵盖高清电影电视剧、超清影视播放等内容，画质清晰不模糊，高清不卡顿影视体验。',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+86-400-888-8888',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
  },
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '88影视网',
  url: 'https://www.88yingshi.com',
  description: '88影视网 - 高清影视在线观看 | 高清电影电视剧超清影视播放不卡顿',
  inLanguage: 'zh-CN',
};

const webpageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: '88影视网 - 高清影视在线观看',
  description: '88影视网提供高清影视在线观看资源，涵盖高清电影电视剧、超清影视播放等内容，高清不卡顿影视体验。',
  inLanguage: 'zh-CN',
};

function getArticleSchema(articleData?: Record<string, unknown>): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: articleData?.headline || '影视详情',
    description: articleData?.description || '',
    author: {
      '@type': 'Organization',
      name: '88影视网',
    },
    publisher: {
      '@type': 'Organization',
      name: '88影视网',
    },
    datePublished: articleData?.datePublished || '2026-01-01',
    inLanguage: 'zh-CN',
  };
}

function getFAQPageSchema(faqData?: Record<string, unknown>): Record<string, unknown> {
  const faqs = (faqData?.faqs as Array<{ question: string; answer: string }>) || [];
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

export default function SchemaOrg({ type, data }: SchemaOrgProps) {
  const location = useLocation();

  let schema: Record<string, unknown>;

  switch (type) {
    case 'Organization':
      schema = organizationSchema;
      break;
    case 'WebSite':
      schema = websiteSchema;
      break;
    case 'WebPage':
      schema = { ...webpageSchema, url: window.location.href };
      break;
    case 'BreadcrumbList':
      schema = getBreadcrumbList();
      break;
    case 'Article':
      schema = getArticleSchema(data);
      break;
    case 'FAQPage':
      schema = getFAQPageSchema(data);
      break;
    default:
      schema = {};
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}